import React from 'react'
import { List, useTable, ShowButton, EditButton, DeleteButton, DateField } from '@refinedev/antd'
import { useList, useInvalidate } from '@refinedev/core'
import { Table, Space, Typography } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand, money } from '../../theme/brand'
import { renderStatus, renderAvatarName, renderMoney } from '../../theme/columns'
import { useRowShow, ListPage, Pill, TableSurface, type Stat } from '../../theme/listkit'
import { HandHeart, BarChart3, Target, Activity } from '../../components/icons'

type Donation = Tables<'donations'>
type Profile = Tables<'profiles'>
// The admin summary view's `id` column is nullable in the generated types, which
// conflicts with refine's BaseRecord; declare just the deck columns we read.
type DonationsSummary = {
  raised: number | null
  total_gifts: number | null
  settled_count: number | null
  avg_gift: number | null
  recurring_count: number | null
}

const compact = (n: number) =>
  n >= 1000 ? '$' + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'K' : money(Math.round(n))

export function DonationList() {
  const invalidate = useInvalidate()

  // LIVE opt-in (the global liveMode stays 'off' — see App.tsx). Gifts are the
  // one list on this admin that nobody in this tab causes: a Cheddar Up payment
  // arrives from the cheddarup-webhook Edge Function minutes after an admin last
  // touched anything. Without a subscription the gift is invisible here until a
  // hard reload — staleTime is 5min and refetchOnWindowFocus is off, so tabbing
  // away and back is NOT enough. `donations` is in the supabase_realtime
  // publication with REPLICA IDENTITY FULL, and an admin passes the
  // "donations: admin select all" policy that Realtime re-evaluates per
  // subscriber, so the INSERT does reach this page. One bounded page of rows per
  // refetch — the same cheap opt-in the events list makes.
  const { tableProps } = useTable<Donation>({
    syncWithLocation: true,
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    liveMode: 'auto',
    // The stat deck below reads admin_donations_summary, which is a VIEW — and
    // views are not in the publication (verified against prod: relkind='v', and
    // it is absent from pg_publication_tables). Putting liveMode on its useList
    // would subscribe to a channel that can never fire, leaving "Total Raised"
    // stale beside a row that just appeared. Drive it from this one donations
    // subscription instead, so the table and the deck move together — the same
    // table+summary invalidation pair push_broadcasts/list.tsx uses.
    onLiveEvent: () => invalidate({ resource: 'admin_donations_summary', invalidates: ['list'] }),
  })
  const onRow = useRowShow('donations')

  // Stat deck reads ONE pre-aggregated row from the admin summary view instead
  // of fetching the whole donations table and reducing on the main thread.
  // liveMode stays off here on purpose — see onLiveEvent above.
  const { result: summaryRes } = useList<DonationsSummary>({
    resource: 'admin_donations_summary',
    pagination: { pageSize: 1 },
  })
  const summary = summaryRes?.data?.[0]
  const raised = Number(summary?.raised ?? 0)
  const totalGifts = Number(summary?.total_gifts ?? 0)
  const settledCount = Number(summary?.settled_count ?? 0)
  const avg = Number(summary?.avg_gift ?? 0)
  const recurring = Number(summary?.recurring_count ?? 0)

  // Resolve user_id -> name, scoped to the donors on the CURRENT page only.
  const rows = (tableProps.dataSource ?? []) as Donation[]
  const userIds = React.useMemo(
    () => Array.from(new Set(rows.map((r) => r.user_id).filter(Boolean))) as string[],
    [rows],
  )
  const { result: profileRes } = useList<Profile>({
    resource: 'profiles',
    pagination: { mode: 'off' },
    filters: userIds.length ? [{ field: 'user_id', operator: 'in', value: userIds }] : [],
    queryOptions: { enabled: userIds.length > 0 },
  })
  const profileMap = React.useMemo<Record<string, string>>(() => {
    const items = (profileRes?.data ?? []) as Profile[]
    return Object.fromEntries(items.filter((p) => p.user_id != null).map((p) => [p.user_id as string, p.name]))
  }, [profileRes])

  const stats: Stat[] = [
    { label: 'Total Raised', value: compact(raised), hint: `${settledCount} settled`, icon: HandHeart, tone: 'gold' },
    { label: 'Gifts', value: totalGifts.toLocaleString(), icon: BarChart3, tone: 'jaguar' },
    { label: 'Average Gift', value: settledCount ? compact(avg) : '—', icon: Target, tone: 'gold' },
    { label: 'Recurring', value: recurring.toLocaleString(), hint: 'monthly', icon: Activity, tone: 'green' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Giving · Gifts',
        description:
          'Every gift members make through the app — one-time and recurring. Track totals, resolve donors, and reconcile gift status as payments settle.',
      }}
      stats={stats}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Donation>
              dataIndex="user_id"
              title="Donor"
              // Stripe gifts resolve to a member; Cheddar Up gifts may have no
              // linked user, so fall back to the webhook-captured donor name/email.
              render={(userId: string | null, record: Donation) =>
                renderAvatarName(
                  (userId ? profileMap[userId] : null) ??
                    record.donor_name ??
                    record.donor_email ??
                    userId ??
                    '—',
                )
              }
            />
            <Table.Column<Donation>
              dataIndex="provider"
              title="Provider"
              render={(p: string | null) => {
                const key = p ?? 'stripe'
                const label = key === 'cheddarup' ? 'Cheddar Up' : key === 'mock' ? 'Mock' : 'Stripe'
                const tone = key === 'cheddarup' ? 'gold' : key === 'mock' ? 'neutral' : 'jaguar'
                return <Pill tone={tone}>{label}</Pill>
              }}
            />
            <Table.Column<Donation>
              dataIndex="fund"
              title="Fund"
              render={(fund: string | null) =>
                fund ? <span style={{ fontFamily: brand.fSans, fontWeight: 600, color: brand.ink }}>{fund}</span> : '—'
              }
            />
            <Table.Column<Donation> dataIndex="amount" title="Amount" sorter render={renderMoney} />
            <Table.Column<Donation>
              dataIndex="frequency"
              title="Frequency"
              render={(f: string | null) =>
                f === 'monthly' ? <Pill tone="gold">Monthly</Pill> : <Pill tone="neutral">One-time</Pill>
              }
            />
            <Table.Column<Donation> dataIndex="status" title="Status" render={renderStatus} />
            <Table.Column<Donation>
              dataIndex="stripe_payment_intent_id"
              title="Payment Intent"
              render={(id: string | null) =>
                id ? (
                  <Typography.Text
                    copyable={{ text: id }}
                    ellipsis={{ tooltip: id }}
                    style={{ fontFamily: brand.fSans, fontSize: 12, color: brand.sub, maxWidth: 140 }}
                  >
                    {id}
                  </Typography.Text>
                ) : (
                  <span style={{ color: brand.sub }}>—</span>
                )
              }
            />
            <Table.Column<Donation>
              dataIndex="created_at"
              title="Created"
              sorter
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Donation>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Donation) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
